import { useState, useEffect } from 'react';
import { ArrowUpCircle, ArrowDownCircle, Clock, Target } from 'lucide-react';
import ModuleContainer from '../layout/ModuleContainer.jsx';

const STATUS_COLORS = {
  ACTIVE:   'bg-korinth-accent/10 text-korinth-accent',
  FILLED:   'bg-blue-500/10 text-blue-400',
  STOPPED:  'bg-korinth-danger/10 text-korinth-danger',
  TP1_HIT:  'bg-korinth-accent/10 text-korinth-accent',
  TP2_HIT:  'bg-green-400/10 text-green-400',
  TP3_HIT:  'bg-yellow-400/10 text-yellow-400',
  EXPIRED:  'bg-korinth-muted/10 text-korinth-muted',
};

function ScoreBar({ label, value, max = 2 }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] font-mono text-korinth-muted w-20">{label}</span>
      <div className="flex-1 h-1.5 bg-korinth-bg rounded-full overflow-hidden">
        <div
          className="h-full bg-korinth-accent rounded-full"
          style={{ width: `${(value / max) * 100}%` }}
        />
      </div>
      <span className="text-[10px] font-mono text-korinth-text w-6 text-right">{value}/{max}</span>
    </div>
  );
}

export default function SignalCard({ instrument, onShadowTrade }) {
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchActive() {
      try {
        const res = await fetch(`/api/futures-signals/active?instrument=${instrument}`);
        if (res.ok) setSignals(await res.json());
      } catch { /* silent */ }
      finally { setLoading(false); }
    }
    fetchActive();
    const interval = setInterval(fetchActive, 15000);
    return () => clearInterval(interval);
  }, [instrument]);

  const signal = signals[0] || null;

  return (
    <ModuleContainer title="Active Signal" subtitle="Real-time OTE entry detection">
      {signal ? (
        <div className="space-y-4">
          {/* Direction + Status */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {signal.direction === 'LONG' ? (
                <ArrowUpCircle className="w-8 h-8 text-korinth-accent" />
              ) : (
                <ArrowDownCircle className="w-8 h-8 text-korinth-danger" />
              )}
              <div>
                <h3 className={`text-xl font-mono font-bold ${signal.direction === 'LONG' ? 'text-korinth-accent' : 'text-korinth-danger'}`}>
                  {signal.direction}
                </h3>
                <p className="text-[10px] text-korinth-muted">{instrument}</p>
              </div>
            </div>
            <span className={`px-2 py-1 rounded text-[10px] font-mono font-bold ${STATUS_COLORS[signal.status] || ''}`}>
              {signal.status}
            </span>
          </div>

          {/* Price levels */}
          <div className="grid grid-cols-2 gap-2">
            {[
              { label: 'Entry', value: signal.entry_price, color: 'text-white' },
              { label: 'Stop Loss', value: signal.stop_loss, color: 'text-korinth-danger' },
              { label: 'TP1 (1:1)', value: signal.take_profit_1, color: 'text-korinth-accent' },
              { label: 'TP2 (2:1)', value: signal.take_profit_2, color: 'text-green-400' },
              { label: 'TP3 (3:1)', value: signal.take_profit_3, color: 'text-yellow-400' },
              { label: 'Score', value: signal.score, color: 'text-white', suffix: '/10' },
            ].map(({ label, value, color, suffix }) => (
              <div key={label} className="bg-korinth-bg rounded px-3 py-2">
                <div className="text-[10px] text-korinth-muted">{label}</div>
                <div className={`text-sm font-mono font-bold ${color}`}>
                  {value != null ? (suffix ? `${value}${suffix}` : parseFloat(value).toFixed(2)) : '—'}
                </div>
              </div>
            ))}
          </div>

          {/* Score breakdown */}
          {signal.score_breakdown && (
            <div className="space-y-1.5">
              <h4 className="text-[10px] font-mono text-korinth-muted uppercase tracking-wider">Score Breakdown</h4>
              <ScoreBar label="HTF Bias" value={signal.score_breakdown.htf_bias} />
              <ScoreBar label="Profile" value={signal.score_breakdown.profile_confidence} />
              <ScoreBar label="OTE Zone" value={signal.score_breakdown.ote_zone} />
              <ScoreBar label="FVG" value={signal.score_breakdown.fvg_confluence} />
              <ScoreBar label="Calendar" value={signal.score_breakdown.calendar_clear} />
            </div>
          )}

          {/* Shadow trade button */}
          <button
            onClick={() => onShadowTrade?.(signal)}
            className="w-full py-2 rounded-lg bg-korinth-accent/10 text-korinth-accent text-xs font-mono font-bold border border-korinth-accent/30 hover:bg-korinth-accent/20 transition-colors"
          >
            Log Shadow Trade
          </button>
        </div>
      ) : (
        <div className="text-center py-8">
          <Target className="w-8 h-8 text-korinth-muted mx-auto mb-2" />
          <p className="text-xs font-mono text-korinth-muted">Monitoring for OTE entry...</p>
          <p className="text-[10px] text-korinth-muted/60 mt-1">Signal fires when score &ge; 7/10 in golden zone</p>
        </div>
      )}
    </ModuleContainer>
  );
}
